"use client";

import Link from "next/link";
import {
  ArrowRight,
  BadgeCheck,
  Camera,
  Clock,
  MapPin,
  Smartphone,
  Store,
  Upload,
  Users,
} from "lucide-react";
import { useMemo } from "react";
import { AppStoreDownloadButtons } from "@/components/marketing/AppStoreDownloadButtons";
import {
  InteractiveAppShowcase,
  type ShowcaseCard,
} from "@/components/marketing/InteractiveAppShowcase";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { MarketingHeroSecondaryLinks } from "@/components/marketing/MarketingHeroSecondaryLinks";
import { MarketingTrustPillRows } from "@/components/marketing/MarketingTrustPillRows";
import { HomeRestaurantPreviewSection } from "@/components/marketing/HomeRestaurantPreviewSection";
import { getHomeShowcaseImagePaths } from "@/lib/home-showcase-images";
import { useHomeShowcaseImageTheme } from "@/lib/useHomeShowcaseImageTheme";
import { useLanguage } from "@/lib/useLanguage";

const HOW_ICONS = [MapPin, Camera, Upload] as const;
const DIFF_ICONS = [Users, Clock, BadgeCheck, Smartphone] as const;

export function HomePageContent() {
  const { language, t } = useLanguage();
  const imageTheme = useHomeShowcaseImageTheme();
  const h = t.homePage;

  const barrios = h.neighborhoods.items.map((item) => ({
    href: `/${language}/${item.slug}`,
    label: item.label,
  }));

  const showcaseCards = useMemo((): ShowcaseCard[] => {
    const showcaseImages = getHomeShowcaseImagePaths(language, imageTheme);
    const imageKeys = [
      "findMenu",
      "menuPhotos",
      "shareLists",
      "alerts",
    ] as const;
    return h.hero.showcase.cards.map((card, index) => ({
      id: `showcase-${index}`,
      title: card.title,
      subtitle: card.subtitle,
      imageSrc: showcaseImages[imageKeys[index] ?? "findMenu"],
      imageAlt: card.imageAlt,
    }));
  }, [language, h.hero.showcase.cards, imageTheme]);

  return (
    <div key={language} className="min-h-screen flex flex-col bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <MarketingHeader locale={language} />

      <main className="flex-1">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-12 pb-16">
          <section className="mb-14 lg:mb-16 lg:pb-4">
            <div className="grid grid-cols-1 gap-10 lg:gap-12 items-center lg:items-start lg:grid-cols-[minmax(0,1fr)_minmax(536px,0.95fr)] xl:grid-cols-[minmax(0,1fr)_minmax(584px,0.95fr)]">
              <div className="min-w-0 text-center lg:text-left">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white leading-[1.12] mb-4 max-w-xl mx-auto lg:mx-0">
                  {h.hero.title}
                </h1>
                <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed mb-6 max-w-xl mx-auto lg:mx-0">
                  {h.hero.subtitle}
                </p>

                <div className="flex flex-col gap-4 justify-center lg:justify-start max-w-md w-full mx-auto lg:mx-0">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {h.hero.downloadLabel}
                  </p>
                  <AppStoreDownloadButtons />
                  <Link
                    href="/explore"
                    className="inline-flex items-center justify-center gap-2 w-full min-h-16 px-6 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 font-semibold text-base leading-snug transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
                  >
                    {h.hero.exploreMapCta}
                    <MapPin className="w-4 h-4 shrink-0" aria-hidden />
                  </Link>
                  <MarketingHeroSecondaryLinks
                    links={[
                      {
                        href: `/${language}/menu-del-dia-barcelona`,
                        label: h.hero.exploreMenus,
                        icon: <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />,
                      },
                      {
                        href: "/restaurants",
                        label: h.hero.forRestaurants,
                        icon: <Store className="w-4 h-4 shrink-0" aria-hidden />,
                      },
                    ]}
                  />
                </div>

                <MarketingTrustPillRows pills={h.trust.pills} />
              </div>

              <div className="min-w-0 w-full">
                <InteractiveAppShowcase
                  key={`${language}-${imageTheme}`}
                  cards={showcaseCards}
                  className="w-full"
                />
              </div>
            </div>
          </section>

          <section id="como-funciona" className="mb-14 lg:mb-16 scroll-mt-20">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2 text-center lg:text-left">
              {h.howItWorks.title}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base mb-8 max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
              {h.howItWorks.subtitle}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
              {h.howItWorks.steps.map((step, index) => {
                const Icon = HOW_ICONS[index] ?? MapPin;
                return (
                  <article
                    key={step.title}
                    className="rounded-xl bg-slate-100/70 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 p-5 sm:p-6"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600/15 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400">
                        <Icon className="w-5 h-5" aria-hidden />
                      </span>
                      <span className="text-xs font-semibold text-slate-500">
                        {h.howItWorks.stepLabel} {index + 1}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{step.title}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{step.description}</p>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="mb-14 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-8 text-center lg:text-left">
              {h.differentiators.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {h.differentiators.items.map((item, index) => {
                const Icon = DIFF_ICONS[index] ?? Users;
                return (
                  <article
                    key={item.title}
                    className="rounded-xl bg-slate-100/60 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/50 p-5 flex gap-4"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-600/15 text-emerald-600 dark:text-emerald-400">
                      <Icon className="w-5 h-5" aria-hidden />
                    </span>
                    <div>
                      <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{item.description}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <HomeRestaurantPreviewSection />

          <section id="barrios" className="mb-14 lg:mb-16 scroll-mt-20">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2 text-center lg:text-left">
              {h.neighborhoods.title}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base mb-6 max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
              {h.neighborhoods.subtitle}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {barrios.map((barrio) => (
                <Link
                  key={barrio.href}
                  href={barrio.href}
                  className="rounded-lg bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/50 px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700/60 hover:text-slate-900 dark:hover:text-white transition-colors flex items-center justify-between gap-2"
                >
                  {barrio.label}
                  <ArrowRight className="w-3.5 h-3.5 text-slate-500 shrink-0" aria-hidden />
                </Link>
              ))}
            </div>
          </section>

          <section className="rounded-2xl bg-gradient-to-br from-blue-600/10 to-emerald-600/5 dark:from-blue-600/20 dark:to-emerald-600/10 border border-slate-200 dark:border-slate-700/50 p-6 sm:p-8 lg:p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-3">{h.closing.title}</h2>
            <p className="text-slate-700 dark:text-slate-300 text-sm sm:text-base mb-6 max-w-xl mx-auto">
              {h.closing.subtitle}
            </p>
            <AppStoreDownloadButtons className="justify-center max-w-md mx-auto mb-5" />
            <Link
              href="/explore"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
            >
              {h.closing.openMap}
              <MapPin className="w-4 h-4 shrink-0" aria-hidden />
            </Link>
          </section>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
